/**
 * Entity resolution service API client — canonical entities + provenance.
 *
 * Requests are routed through the gateway (/api/v1/entities/*) which
 * proxies to entity_resolution_service. Entity match reviews use
 * /api/v1/entity-reviews/* (maps to /reviews on the backend).
 */

import { config } from '@/config/environment';
import { getAuthToken } from '@/contexts/AuthContext';
import type {
  EntityDetailResponse,
  EntityListResponse,
  TemporalEntityState,
} from '@/types/api';
import axios from 'axios';

const entityApi = axios.create({
  baseURL: config.GATEWAY_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

entityApi.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

// ---------------------------------------------------------------------------
// Entity list + detail
// ---------------------------------------------------------------------------

export interface EntityListParams {
  entity_type?: string;
  is_active?: boolean;
  /** XC-ERQ2 D14: current SCD2 rows only (census click-in parity, AC-C2). */
  current?: boolean;
  /** AW-121 U2 row 9: one group of a role tile's split (with current and a customer, vendor or employee type). */
  group?: TileGroup;
  page?: number;
  page_size?: number;
}

/** A role tile's click-in groups: what it counts, vendors on his list with no purchase, names only seen. */
export type TileGroup = 'counted' | 'list_only' | 'seen';

/** List canonical entities with optional filters. */
export async function fetchEntities(
  params?: EntityListParams,
): Promise<EntityListResponse> {
  const { data } = await entityApi.get<EntityListResponse>(
    '/api/v1/entities',
    { params },
  );
  return data;
}

/** Get a single entity with provenance and source links. */
export async function fetchEntity(
  entityId: string,
): Promise<EntityDetailResponse> {
  const { data } = await entityApi.get<EntityDetailResponse>(
    `/api/v1/entities/${entityId}`,
  );
  return data;
}

/** Get entity state at a specific point in time. */
export async function fetchTemporalEntity(
  entityId: string,
  entityType: string,
  asOf: string,
): Promise<TemporalEntityState> {
  const { data } = await entityApi.get<TemporalEntityState>(
    `/api/v1/temporal/${entityType}/${entityId}/at`,
    { params: { as_of: asOf } },
  );
  return data;
}
